import { useQuery } from '@tanstack/react-query'
import { getSubstation, getFeeders } from '../../api/topology.ts'

interface SubstationDetailProps {
  id: number
  onNavigate: (type: string, id: number) => void
}

export function SubstationDetail({ id, onNavigate }: SubstationDetailProps) {
  const { data: substation, isLoading } = useQuery({
    queryKey: ['substation', id],
    queryFn: () => getSubstation(id),
  })

  const { data: feeders } = useQuery({
    queryKey: ['substation-feeders', id],
    queryFn: () => getFeeders({ substation_id: id, limit: 50 }),
    enabled: !!substation,
  })

  if (isLoading) {
    return <div className="p-4 text-sm text-gray-400">Loading substation...</div>
  }

  if (!substation) {
    return <div className="p-4 text-sm text-red-400">Substation not found</div>
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        <div className="text-base font-semibold text-gray-900">
          {substation.name || `Substation #${substation.id}`}
        </div>
        {substation.code && (
          <div className="text-sm text-gray-500">Code: {substation.code}</div>
        )}
      </div>

      {/* Key details */}
      <div className="space-y-1.5 text-sm">
        <DetailRow label="GIS ID" value={substation.gis_id} />
        <DetailRow label="Primary (kV)" value={substation.voltage_primary_kv} />
        <DetailRow label="Secondary (kV)" value={substation.voltage_secondary_kv} />
        <DetailRow label="Feeders" value={substation.feeder_count} />
      </div>

      {/* Feeders list */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Feeders ({substation.feeder_count})
        </p>
        {feeders && feeders.length > 0 ? (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {feeders.map(f => (
              <button
                key={f.id}
                onClick={() => onNavigate('Feeder', f.id)}
                className="w-full text-left px-2 py-1 rounded text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                <span className="text-gray-700 truncate flex-1">
                  {f.name || f.code || `Feeder #${f.id}`}
                </span>
                <span className="text-xs text-gray-400">
                  {f.transformer_count} xfmrs
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-400">No feeders</div>
        )}
      </div>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined) return null
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 font-medium">{String(value)}</span>
    </div>
  )
}
